import { deriveVisitPaymentState } from '@/lib/appointmentPaymentState';
import { displayPaymentMethods } from '@/lib/appointmentSettlement';
import type { AppointmentLifecycleStatus } from '@/utils/appointmentStatus';

export type PatientAppointmentChip =
  | { kind: 'refunded' }
  | { kind: 'paid'; methods: string[] }
  | { kind: 'partial' }
  | { kind: 'pending' }
  | { kind: 'unpaid_invoice' }
  | { kind: 'none' };

export type PatientAppointmentLink = {
  kind: 'invoice' | 'credit_note';
  id: string;
  number: string | null;
} | null;

/**
 * Patient → Aftaler payment chip and link.
 * A refunded visit uses the same payment state as the modal and the day list:
 * the credit note is not the visit invoice, Refunderet is the chip, and the
 * link is the kreditnota.
 */
export function patientAppointmentRowBilling(input: {
  lifecycle: AppointmentLifecycleStatus;
  invoiceStatus?: string | null;
  paymentStatus?: string | null;
  paymentMethod?: string | null;
  paymentMethods?: Array<string | null | undefined>;
  invoiceId?: string | null;
  invoiceNumber?: string | null;
  creditNoteId?: string | null;
  creditNoteNumber?: string | null;
}): { chip: PatientAppointmentChip; link: PatientAppointmentLink } {
  const state = deriveVisitPaymentState({
    lifecycle: input.lifecycle,
    invoiceStatus: input.invoiceStatus,
    paymentStatus: input.paymentStatus,
    punchCardHeld: input.paymentMethod === 'punch_card' && input.paymentStatus === 'paid',
  });
  if (state === 'refunded') {
    return {
      chip: { kind: 'refunded' },
      link: input.creditNoteId
        ? {
            kind: 'credit_note',
            id: input.creditNoteId,
            number: input.creditNoteNumber ?? null,
          }
        : null,
    };
  }

  const invoiceLink: PatientAppointmentLink = input.invoiceId
    ? { kind: 'invoice', id: input.invoiceId, number: input.invoiceNumber ?? null }
    : null;
  const methods = displayPaymentMethods([
    ...(input.paymentMethods ?? []),
    input.paymentMethod,
  ]);
  if (state === 'partly_paid' || input.invoiceStatus === 'partial') {
    return { chip: { kind: 'partial' }, link: invoiceLink };
  }
  if (state === 'paid' || input.paymentStatus === 'paid' || input.invoiceStatus === 'paid') {
    return { chip: { kind: 'paid', methods }, link: invoiceLink };
  }
  if (input.paymentStatus === 'pending') return { chip: { kind: 'pending' }, link: invoiceLink };
  if (input.invoiceId) return { chip: { kind: 'unpaid_invoice' }, link: invoiceLink };
  return { chip: { kind: 'none' }, link: null };
}
